<template>
    <div v-if="showLoader" class="text-center">
        <b-spinner variant="primary" class="my-2" />
    </div>
    <div v-else>
        <div v-if="errorLoading" class="error-message w-100 pb-3 text-center">{{ errorLoading }}</div>
        <div>
            <b-table
                ref="placeholdersTable"
                class="w-100 h-100 items-table"
                table-class="h-100"
                striped
                hover
                :items="uniquePlaceholders"
                select-mode="multi"
                :fields="uniquePlaceholdersTableFields"
                responsive
                show-empty
                empty-text="No items"
                selectable
                @row-selected="onUniquePlaceholderSelected"
            >
                <template #cell(name)="data">
                    <div v-if="props.podService == 'printify'" class="text-capitalize">
                        {{ data.item.placeholder.position.replace('_', ' ') }}
                    </div>
                    <div v-if="props.podService == 'printful'" class="align-items-center d-flex h-100">
                        <div>{{ data.item.placeholder.title }} ({{ data.item.placeholder.position }})</div>
                    </div>
                </template>

                <template #cell(size)="data">
                    <placeholder-size :placeholder="getBiggestPlaceholder(data.item.placeholder)" />
                </template>

                <template #cell(ratio)="data">
                    <div>
                        {{ formatAspectRatio(data.item.placeholder) }}
                    </div>
                </template>

                <template #cell(variantsQuantity)="data">
                    <div class="text-capitalize">{{ data.item.variants.length }}</div>
                </template>
            </b-table>
        </div>

        <!-- Detailed variants view -->
        <div v-if="!errorLoading" class="primary-text">
            <a href="#" @click="showVariantsDetails = !showVariantsDetails">Toggle detailed view</a>
        </div>
        <div v-if="showVariantsDetails">
            <div v-for="(providerVariants, providerId) in variants" :key="providerId">
                <div class="variant-table-title py-2 px-3">
                    <b v-if="props.podService === 'printify'">
                        Select one or more product variants from "{{ getPrintProviderName(providerId) }}"
                    </b>
                    <b v-else> Select one or more product variants </b>
                </div>
                <variant-selector
                    v-model="selectedVariants[providerId]"
                    title=""
                    :variants="providerVariants"
                    :show-done-button="false"
                    :row-class-setter="(row) => getCssClassesForVariantRow(row, String(providerId))"
                >
                    <!-- Placeholders selection -->
                    <template #row-details="{ data }">
                        <div class="card">
                            <div class="card-body">
                                <multi-select-table
                                    v-model="selectedPlaceholdersData[providerId][data.id]"
                                    :show-done-button="false"
                                    :fields="placeholdersTableFields"
                                    :items="getPlaceholdersForVariant(String(providerId), data.id)"
                                    @selection-finished="onPlaceholdersSelectionFinished"
                                >
                                    <template #cell(name)="data">
                                        {{ data.item.position.replace('_', ' ') }}
                                        ({{ data.item.width }}x{{ data.item.height }})
                                    </template>
                                </multi-select-table>
                            </div>
                        </div>
                    </template>
                </variant-selector>
                <hr />
            </div>
        </div>
        <div v-if="errorSaving" class="error-message w-100 pb-3 text-center">{{ errorSaving }}</div>
        <button v-if="errorLoading" class="btn btn-outline-danger" @click="deleteTemplateProduct">
            Delete Template Product
        </button>
        <button
            class="btn btn-primary float-right"
            :disabled="!anySelectedPlaceholders"
            @click="onPlaceholdersSelectionFinished"
        >
            Done
        </button>
    </div>
</template>

<script lang="ts" setup>
import { getPrintProviderVariants } from '@/api/storesApp/printify';
import { PrintProviderClass, SelectedProviderVariants, SelectedVariant } from '@/typings/printify/printProvider';
import { Placeholder } from './typings';
import { formatAspectRatio as utilFormatAspectRatio } from '@/utils/formatUtils';
import type { GenericPodVariant } from '../VariantSelector/typings';
import { Ref, ref, computed, onMounted, watch } from 'vue';
import { PodProviderConfiguration, PodProviderPlaceholderConfiguration } from '../typings';
import { Vue } from 'vue-property-decorator';
import { BTableRow } from '@/typings/bootstrap/table';
import MultiSelectTable from '@/components/MultiSelectTable/MultiSelectTable.vue';
import VariantSelector from '@/components/TemplateProductPodAssigner/VariantSelector/VariantSelector.vue';
import PlaceholderSize from '@/components/TemplateProductPodAssigner/PrintifyConfigurator/PlaceholderSize/PlaceholderSize.vue';
import { PodService } from '@/api/typings/templateProducts';
import { getPrintfulPrintFiles, getProduct } from '@/api/storesApp/printful';
import type { File } from '@/typings/printful/product';
import { printProviderFake } from '../printProviderFake';
import { UniquePlaceholder, VariantsModalInit, VariantsResult } from './typings';
import { BTable } from 'bootstrap-vue';
import { Provider } from '@/utils/PodProvider.enum';
import { getReasonErrorMessage } from '@/utils/promiseUtils';
import { deleteTemplateProduct as _deleteTemplateProduct } from '@/api/admin/templateProducts';

const emit = defineEmits<{
    (
        event: 'variants-selected',
        value: {
            configurations: PodProviderConfiguration[];
            biggestPlaceholders: Placeholder[];
            selectedProviderVariants: SelectedProviderVariants[];
        }
    ): void;
    (event: 'delete-template-product'): void;
}>();

const props = withDefaults(
    defineProps<{
        podService: PodService;
        selectedPrintProviders?: PrintProviderClass[];
        printfulFiles?: File[];
        selectedProductId: string;
        selectedTemplateId: string;
        store: string;
        configurations: PodProviderConfiguration[];
        showVariantsDetails?: boolean;
        allowedPlaceholders?: string[];
    }>(),
    {
        selectedPrintProviders: () => [],
        printfulFiles: () => [],
        configurations: () => [],
        allowedPlaceholders: () => [],
        showVariantsDetails: () => false
    }
);

onMounted(() => {
    initializeVariantsAndPlaceholders();
});

watch(
    () => props.store,
    (newStore, oldStore) => {
        if (newStore !== oldStore) {
            initializeVariantsAndPlaceholders();
        }
    }
);

const placeholdersTableFields: Record<string, string[]>[] = [{ name: ['Placeholder Position'] }];
const uniquePlaceholdersTableFields = [
    { key: 'name', label: 'Placeholder Position' },
    { key: 'size', label: 'Biggest Area' },
    { key: 'ratio', label: 'Aspect Ratio' },
    { key: 'variantsQuantity', label: 'Used in variants' }
];

const placeholdersTable: Ref<BTable | null> = ref(null);
const variants: Ref<{ [providerId: string]: GenericPodVariant[] }> = ref({});
const showLoader: Ref<boolean> = ref(false);
const selectedVariants: Ref<{ [providerId: string]: GenericPodVariant[] }> = ref({});
const placeholdersData: Ref<{
    [providerId: string]: { [variantId: string]: Placeholder[] };
}> = ref({});
const selectedPlaceholdersData: Ref<{
    [providerId: string]: { [variantId: string]: Placeholder[] };
}> = ref({});
const errorLoading: Ref<string | null> = ref(null);
const errorSaving: Ref<string | null> = ref(null);
const showVariantsDetails: Ref<boolean> = ref(false);

// When selected placeholders change, update the selected variants
watch(
    () => selectedPlaceholdersData.value,
    (newVal) => {
        for (const printProviderId in newVal) {
            selectedVariants.value[printProviderId] = [];
            for (const variantId in selectedPlaceholdersData.value[printProviderId]) {
                if (selectedPlaceholdersData.value[printProviderId][variantId].length) {
                    const variant = variants.value[printProviderId].find((v) => v.id === variantId);
                    if (variant) {
                        selectedVariants.value[printProviderId].push(variant);
                    }
                }
            }
        }
    },
    { deep: true }
);

const initPrintifyVariants = async (provider: PrintProviderClass): Promise<VariantsModalInit> => {
    const providerVariants = (
        await getPrintProviderVariants(Number(props.selectedProductId), Number(provider.id), props.store, true)
    ).variants;

    const genericVariants = providerVariants.map((variant) => {
        return {
            id: variant.id.toString(),
            name: variant.title
        } as GenericPodVariant;
    });
    const selectedVariants = genericVariants.filter((v) => {
        return props.configurations
            .filter((c) => c.productId === props.selectedProductId)
            .flatMap((c) => c.placeholders)
            .map((v) => v.variantId)
            .includes(v.id.toString());
    });

    const placeholdersData = providerVariants.map((v) => {
        return {
            variantId: v.id.toString(),
            placeholders: v.placeholders
        };
    });

    return {
        genericVariants,
        selectedVariants,
        placeholdersData,
        provider
    };
};

const initPrintfulVariants = async (): Promise<VariantsModalInit> => {
    const { variants, product } = await getProduct(props.selectedProductId, props.store);
    const genericVariants = variants.map((variant) => {
        return {
            id: variant.id.toString(),
            name: variant.color ? `${variant.color} / ${variant.size}` : variant.size
        } as GenericPodVariant;
    });
    const selectedVariants = genericVariants.filter((v) => {
        return props.configurations
            .filter((c) => c.productId === props.selectedProductId)
            .flatMap((c) => c.placeholders)
            .map((v) => v.variantId)
            .includes(v.id.toString());
    });
    let placeholdersData: {
        variantId: string;
        placeholders: Placeholder[];
    }[] = [];
    const printFiles = await getPrintfulPrintFiles(props.store, Number(props.selectedProductId));

    let printfulFiles: File[];
    if (props.printfulFiles && props.printfulFiles.length) {
        printfulFiles = [...props.printfulFiles];
    } else {
        printfulFiles = product.files;
    }

    const filteredFiles = printfulFiles!.filter((f) => !['mockup'].includes(f.type));
    for (const file of filteredFiles) {
        const placements = printFiles.variant_printfiles.map((v) => ({
            printFileId: v.placements[file.type],
            variantId: v.variant_id
        }));

        for (const placement of placements) {
            const variant = genericVariants.find((v) => v.id == String(placement.variantId));
            const printFile = printFiles.printfiles.find((f) => placement.printFileId === f.printfile_id);

            if (!printFile || !variant) continue;

            const item = placeholdersData.find((x) => x.variantId == variant.id);
            const placeholderToAdd = {
                position: file.type,
                title: file.title,
                height: printFile.height,
                width: printFile.width,
                dpi: printFile.dpi,
                aspectRatio: utilFormatAspectRatio(printFile.width, printFile.height),
                file: file,
                name: `${file.title} (${printFile.width} x ${printFile.height})`
            };
            if (item) {
                item.placeholders.push(placeholderToAdd);
            } else {
                placeholdersData.push({ variantId: String(variant.id), placeholders: [placeholderToAdd] });
            }
        }
    }
    return {
        genericVariants,
        selectedVariants,
        placeholdersData,
        provider: printProviderFake
    };
};

const getInitPromise = async (provider?: PrintProviderClass): Promise<VariantsResult> => {
    let result: VariantsModalInit;
    if (props.podService == 'printify') {
        result = await initPrintifyVariants(provider!);
    } else {
        result = await initPrintfulVariants();
    }
    return {
        providerVariants: result.genericVariants,
        selectedVariants: result.selectedVariants,
        placeholdersData: result.placeholdersData,
        provider: result.provider
    };
};

const initializeVariantsAndPlaceholders = async () => {
    showLoader.value = true;
    variants.value = {};
    selectedVariants.value = {};
    placeholdersData.value = {};
    selectedPlaceholdersData.value = {};
    errorLoading.value = null;

    const promises = [];
    if (props.podService == 'printify' && props.selectedPrintProviders) {
        for (const provider of props.selectedPrintProviders) {
            promises.push(getInitPromise(provider));
        }
    } else {
        promises.push(getInitPromise());
    }

    const results = await Promise.allSettled(promises);
    for (const result of results) {
        if (result.status === 'fulfilled') {
            Vue.set(variants.value, result.value.provider.id, result.value.providerVariants);
            Vue.set(selectedVariants.value, result.value.provider.id, result.value.selectedVariants);

            for (const data of result.value.placeholdersData) {
                if (!placeholdersData.value[result.value.provider.id]) {
                    Vue.set(placeholdersData.value, result.value.provider.id, {});
                }

                placeholdersData.value[result.value.provider.id][data.variantId] = data.placeholders;
            }

            // Make sure that object definition for selected placeholders in this provider is available
            if (!selectedPlaceholdersData.value[result.value.provider.id]) {
                Vue.set(selectedPlaceholdersData.value, result.value.provider.id, {});
            }

            // Retrieve all the selected placeholders for this print provider
            const selectedPlaceholders = props.configurations
                .filter((c) => c.productId === props.selectedProductId)
                .flatMap((c) => c.placeholders)
                .filter((p) => p.printProviderId === result.value.provider.id.toString());

            for (const selectedPlaceholder of selectedPlaceholders) {
                if (typeof selectedPlaceholder.variantId !== 'undefined') {
                    // Check if there's placeholder data from this provider and the variant of this placeholder
                    let existingSelectedPlaceholderData =
                        selectedPlaceholdersData.value[result.value.provider.id][selectedPlaceholder.variantId];

                    if (!existingSelectedPlaceholderData) {
                        Vue.set(
                            selectedPlaceholdersData.value[result.value.provider.id],
                            selectedPlaceholder.variantId,
                            []
                        );
                        existingSelectedPlaceholderData =
                            selectedPlaceholdersData.value[result.value.provider.id][selectedPlaceholder.variantId];
                    }

                    // Find the placeholder element from this provider for this placeholder id and variant id
                    const placeholder = placeholdersData.value[result.value.provider.id]?.[
                        selectedPlaceholder.variantId
                    ]?.find((p) => p.position === selectedPlaceholder.placeholderId);

                    // Add the placeholder to the selected ones only if it doesn't exist already
                    if (
                        placeholder &&
                        !existingSelectedPlaceholderData.some((p) => p.position === placeholder.position)
                    ) {
                        existingSelectedPlaceholderData.push(placeholder);
                    }
                }
            }
        } else {
            const reasonMessage = getReasonErrorMessage(result.reason);
            let errorMessage = '';
            if (reasonMessage.includes('status code 404')) {
                errorMessage = `because the product${
                    props.podService === 'printify' ? ' or print provider' : ''
                } no longer exists`;
            }
            errorLoading.value = `Error loading variants from one or more providers ${errorMessage}`;
            console.error(reasonMessage);
        }
    }
    if (props.showVariantsDetails) {
        showVariantsDetails.value = props.showVariantsDetails;
    }

    showLoader.value = false;
};

const uniquePlaceholders = computed<UniquePlaceholder[]>(() => {
    let allVariants: GenericPodVariant[] = [];
    for (const printProviderId in variants.value) {
        allVariants = [...allVariants, ...variants.value[printProviderId]];
    }

    const uniquePlaceholders: UniquePlaceholder[] = [];
    for (const printProviderId in placeholdersData.value) {
        for (const variantId in placeholdersData.value[printProviderId]) {
            const placeholders = placeholdersData.value[printProviderId][variantId];
            for (const placeholder of placeholders) {
                const existingPlaceholder = uniquePlaceholders.find(
                    (p) =>
                        p.placeholder.position === placeholder.position &&
                        areSameAspectRatio(p.placeholder, placeholder)
                );
                const variant = allVariants.find((v) => v.id === variantId);

                if (variant) {
                    if (existingPlaceholder) {
                        existingPlaceholder.variants.push({
                            printProvider: printProviderId,
                            variant: variant
                        });
                    } else {
                        uniquePlaceholders.push({
                            placeholder: placeholder,
                            variants: [
                                {
                                    printProvider: printProviderId,
                                    variant: variant
                                }
                            ]
                        });
                    }
                }
            }
        }
    }

    return uniquePlaceholders;
});

const anySelectedPlaceholders = computed<boolean>(() => {
    for (const providerId in selectedPlaceholdersData.value) {
        for (const variantId in selectedPlaceholdersData.value[providerId]) {
            if (selectedPlaceholdersData.value[providerId][variantId].length) {
                return true;
            }
        }
    }
    return false;
});

const onUniquePlaceholderSelected = (items: UniquePlaceholder[]) => {
    // Remove all selections
    for (const printProviderId in selectedPlaceholdersData.value) {
        selectedVariants.value[printProviderId] = [];
        for (const variantId in selectedPlaceholdersData.value[printProviderId]) {
            if (selectedPlaceholdersData.value[printProviderId][variantId].length) {
                Vue.set(selectedPlaceholdersData.value[printProviderId], variantId, []);
            }
        }
    }
    for (const item of items) {
        selectAllVariantsWithPlaceholder(item.placeholder);
    }
};

const selectAllVariantsWithPlaceholder = (placeholder: Placeholder) => {
    // Select all the placeholders that have the given position and same aspect ratio across all print providers and variants
    for (const providerId in variants.value) {
        const providerVariants = variants.value[providerId];
        for (const variant of providerVariants) {
            const placeholders = getPlaceholdersForVariant(providerId, variant.id);

            const targetPlaceholder = placeholders.find(
                (p) => p.position === placeholder.position && areSameAspectRatio(p, placeholder)
            );
            const alreadySelected = selectedPlaceholdersData.value[providerId][variant.id]?.some(
                (p) => p.position === placeholder.position && areSameAspectRatio(p, placeholder)
            );

            if (targetPlaceholder && !alreadySelected) {
                if (!selectedPlaceholdersData.value[providerId][variant.id]) {
                    Vue.set(selectedPlaceholdersData.value[providerId], variant.id, []);
                }
                selectedPlaceholdersData.value[providerId][variant.id].push(targetPlaceholder);
            }
        }
    }
};

const getPlaceholdersForVariant = (providerId: string, variantId: string): Placeholder[] => {
    return placeholdersData.value[providerId]?.[variantId] || [];
};

const areSameAspectRatio = (p1: Placeholder, p2: Placeholder) => {
    return (p1.width / p1.height).toFixed(2) === (p2.width / p2.height).toFixed(2);
};

const getBiggestPlaceholder = (searchTarget: Placeholder) => {
    let candidatePlaceholder = searchTarget;
    const candidateArea = candidatePlaceholder.width * candidatePlaceholder.height;

    for (const printProviderId in placeholdersData.value) {
        for (const variantId in placeholdersData.value[printProviderId]) {
            const placeholders = placeholdersData.value[printProviderId][variantId];
            for (const placeholder of placeholders) {
                if (
                    areSameAspectRatio(searchTarget, placeholder) &&
                    placeholder.width * placeholder.height > candidateArea
                ) {
                    candidatePlaceholder = placeholder;
                }
            }
        }
    }

    return candidatePlaceholder;
};

const formatAspectRatio = (placeholder: Placeholder) => {
    return utilFormatAspectRatio(placeholder.width, placeholder.height);
};

const getPrintProviderName = (providerId: string | number): string => {
    if (props.selectedPrintProviders) {
        return props.selectedPrintProviders.find((p) => p.id.toString() === providerId)?.title || '';
    }
    return '';
};

const getCssClassesForVariantRow = (row: BTableRow, providerId: string): string[] => {
    if (selectedVariants.value[providerId]?.some((v) => v.id === (row.item as GenericPodVariant).id)) {
        return ['table-active'];
    }
    return [];
};

const getVariantName = (providerId: string, variantId: string): string => {
    return variants.value[providerId]?.find((v) => v.id === variantId)?.name || '';
};

const validateVariants = (): boolean => {
    const allowed = props.allowedPlaceholders ?? [];
    if (allowed.length === 0) return true;

    const invalidSelection = Object.values(selectedPlaceholdersData.value)
        .flatMap((variants) => Object.values(variants))
        .flat()
        .some((ph) => !allowed.includes(ph.position));

    if (invalidSelection) {
        errorSaving.value = `The selected placeholders must be: ${allowed.join(', ')}`;
        return false;
    }

    const selectedPositions = new Set(
        Object.values(selectedPlaceholdersData.value)
            .flatMap((variants) => Object.values(variants))
            .flat()
            .map((ph) => ph.position)
    );

    const missingAllowed = allowed.some((pos) => !selectedPositions.has(pos));
    if (missingAllowed) {
        errorSaving.value = `The selected placeholders must be: ${allowed.join(', ')}`;
        return false;
    }
    return true;
};

const onPlaceholdersSelectionFinished = () => {
    errorSaving.value = null;
    if (!validateVariants()) {
        return;
    }

    const configurations = [...props.configurations];
    let configuration = configurations.find((c) => c.productId === props.selectedProductId);
    const biggestPlaceholders: Placeholder[] = [];
    const configPlaceholders: PodProviderPlaceholderConfiguration[] = [];
    for (const providerId in selectedPlaceholdersData.value) {
        for (const variantId in selectedPlaceholdersData.value[providerId]) {
            const placeholders = selectedPlaceholdersData.value[providerId][variantId];
            for (const placeholder of placeholders) {
                const item = biggestPlaceholders.find((p) => {
                    return p.position === placeholder.position;
                });

                if (item) {
                    if (placeholder.width * placeholder.height > item.width * item.height) {
                        item.width = placeholder.width;
                        item.height = placeholder.height;
                        (item.dpi = placeholder.dpi),
                            (item.aspectRatio = placeholder.aspectRatio),
                            (item.name = placeholder.name);
                    }
                } else {
                    biggestPlaceholders.push({ ...placeholder });
                }

                configPlaceholders.push({
                    placeholderId: placeholder.position,
                    /* For Printful, we use a fake print provider to reuse the logic built for Printify.
                       When sending the selected data, if the POD service is Printful, we set the printProviderId to undefined.*/
                    printProviderId: props.podService == Provider.PRINTIFY ? providerId : undefined,
                    variantId: variantId
                });
            }
        }
    }

    if (!configuration) {
        const newConfig = {
            provider: props.podService,
            productId: props.selectedProductId,
            placeholders: configPlaceholders
        } as PodProviderConfiguration;

        configurations.push(newConfig);
        configuration = newConfig;
    } else {
        configuration.placeholders = configPlaceholders;
    }

    const selectedProviderVariants: SelectedProviderVariants[] = Object.entries(selectedPlaceholdersData.value).map(
        ([providerId, variantsMap]) => ({
            printProviderId: providerId,
            variants: Object.entries(variantsMap).reduce<SelectedVariant[]>((acc, [variantId, placeholders]) => {
                if (!placeholders || placeholders.length === 0) {
                    return acc;
                }
                const variantName = getVariantName(providerId, variantId);
                const names = placeholders.map((pl) => pl.name ?? '');
                const allHaveNames = names.every((n) => n !== '');
                acc.push({
                    variantId,
                    variantName,
                    placeholders: allHaveNames ? names : placeholders.map((pl) => pl.position)
                });
                return acc;
            }, [])
        })
    );

    emit('variants-selected', {
        configurations,
        biggestPlaceholders,
        selectedProviderVariants
    });
};

const deleteTemplateProduct = async () => {
    try {
        showLoader.value = true;
        await _deleteTemplateProduct(props.selectedTemplateId);
    } finally {
        showLoader.value = false;
    }
    emit('delete-template-product');
};
</script>
